import Modal from "../../../components/modal/Modal";
import { useState } from "react";
import { toast } from "react-toastify";
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";
import {
  Attachment,
  EquipmentHistory,
  deleteEquipmentHistory,
} from "../../../service/api";
import {
  AlertTriangle,
  Clock,
  Download,
  Eye,
  FileText,
  Settings,
  ShoppingCart,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import AddEventModal from "./AddEventModal";

interface ViewEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EquipmentHistory;
  onEditSuccess?: () => void;
}

interface FilePreviewState {
  url: string;
  name: string;
  type: string;
}

const ViewEventModal: React.FC<ViewEventModalProps> = ({
  isOpen,
  onClose,
  event,
  onEditSuccess,
}) => {
  const [fileUrl, setFileUrl] = useState<FilePreviewState>({
    url: "",
    name: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { getsignedurl } = S3TempUrl();

  const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  const getFileIcon = (filename: string) => {
    const extension = getFileExtension(filename);
    switch (extension) {
      case "pdf":
        return <FileText className="w-4 h-4 text-red-500" />;
      case "doc":
      case "docx":
        return <FileText className="w-4 h-4 text-blue-500" />;
      case "jpg":
      case "jpeg":
      case "png":
        return <FileText className="w-4 h-4 text-green-500" />;
      case "txt":
        return <FileText className="w-4 h-4 text-gray-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType) {
      case "Purchase":
        return <ShoppingCart className="w-4 h-4 text-blue-500" />;
      case "Service":
      case "Maintenance":
        return <Settings className="w-4 h-4 text-green-500" />;
      case "Repair":
        return <Wrench className="w-4 h-4 text-orange-500" />;
      case "Calibration":
        return <Settings className="w-4 h-4 text-purple-500" />;
      case "Inspection":
        return <Eye className="w-4 h-4 text-teal-500" />;
      case "Incident":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case "Transfer":
        return <span className="text-gray-500">↔</span>;
      case "Decommission":
        return <Trash2 className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const canPreviewFile = (filename: string): boolean => {
    const extension = getFileExtension(filename);
    return ["pdf", "jpg", "jpeg", "png", "doc", "docx", "txt"].includes(
      extension
    );
  };

  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";
    if (!canPreviewFile(filename)) {
      toast.error("File type not supported for preview");
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      // Get the S3 key from the attachment URL
      const s3Key = attachment.url?.split("/").pop();
      if (!s3Key) {
        throw new Error("Invalid file URL");
      }

      const signedUrl = await getsignedurl(s3Key, getFileExtension(filename));
      setFileUrl({
        url: signedUrl,
        name: filename,
        type: getFileExtension(filename),
      });
      setIsDocumentViewerOpen(true);
    } catch (err: any) {
      console.error("Error previewing file:", err);
      setErrorMessage("Failed to load file. Please try again.");
      toast.error("Failed to preview file");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  const handleEditEvent = () => {
    setIsEditModalOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await deleteEquipmentHistory(event.id);
      toast.success("Event deleted successfully");
      onEditSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Failed to delete event:", error);
      toast.error("Failed to delete event. Please try again.");
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        modalClass="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        closeButton={false}
      >
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-[#61748e]">
            Event Details: {event.eventId || event.id}
          </h2>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Equipment
                </label>
                <p className="text-gray-900 font-medium">
                  {event.equipmentName} ({event.equipmentId})
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type
                </label>
                <div className="flex items-center gap-2">
                  {getEventTypeIcon(event.eventType)}
                  <span className="text-gray-900 font-medium">
                    {event.eventType}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Date
                </label>
                <p className="text-gray-900">
                  {new Date(event.eventDate).toLocaleDateString("en-GB", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cost
                </label>
                <p className="text-gray-900 font-medium">
                  {event.cost
                    ? `₹${event.cost.toLocaleString("en-IN")}`
                    : "Not specified"}
                </p>
              </div>

              {event.performedBy && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Performed By
                  </label>
                  <p className="text-gray-900">{event.performedBy}</p>
                </div>
              )}

              {event.vendor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vendor
                  </label>
                  <p className="text-gray-900">{event.vendor}</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-gray-800">{event.notes}</p>
            </div>
          </div>

          {/* Attachments */}
          {event.attachments && event.attachments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments
              </label>
              <div className="space-y-2">
                {event.attachments.map((attachment, index) => {
                  const filename =
                    attachment.filename ||
                    attachment.name ||
                    attachment.originalName ||
                    attachment.url?.split("/").pop() ||
                    `Attachment ${index + 1}`;

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded border"
                    >
                      <div className="flex items-center space-x-2 flex-1">
                        {getFileIcon(filename)}
                        <span className="text-sm text-gray-700 truncate max-w-xs">
                          {filename}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {canPreviewFile(filename) && (
                          <button
                            onClick={() => handlePreviewFile(attachment)}
                            className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="Preview file"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => window.open(attachment.url, "_blank")}
                          className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="Download file"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t mt-6">
          {/* <button
            onClick={handleEditEvent}
            className="px-6 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            Edit
          </button>
          <button
            onClick={handleDeleteEvent}
            className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Delete
          </button> */}
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && fileUrl.url && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button
                onClick={handleCloseDocumentViewer}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="w-full h-full">
                  {fileUrl.type === "pdf" ? (
                    <PdfViewer fileUrl={fileUrl.url} />
                  ) : (
                    <DocumentViewer file={fileUrl.url} name={fileUrl.name} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {/* {isEditModalOpen && (
        <AddEventModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false);
            onEditSuccess?.();
            onClose();
          }}
          initialData={event}
        />
      )} */}
    </>
  );
};

export default ViewEventModal;
